/**
 * Predefined filter values for configurable insights
 * These correspond to the Python enums in filters_values.py
 */

import type { FilterOption } from '@/types/decision-engine/configurable-insights.types';
import type { ConfigurableInsightLayoutType } from '@/types/decision-engine/configurable-insights.types';
import { ConfigurableInsightLayoutType as InsightLayoutType } from '@/types/decision-engine/configurable-insights.types';

// ============================================================================
// Label Mappings for Enrichment Values
// ============================================================================

/**
 * Display text mappings for transaction categories
 * Maps API enum values to user-friendly display text
 *
 */
export const TRANSACTION_CATEGORY_LABELS: Record<string, string> = {
  ACCOMMODATION: 'Accommodation',
  AUTO_LOAN: 'Auto Loan',
  BANK_CHARGE: 'Bank Charge',
  BUY_NOW_PAY_LATER: 'Buy Now Pay Later',
  CARD_REPAYMENT: 'Card Repayment',
  COMMISSION_RECEIVED: 'Commission Received',
  CRYPTO_TRADING: 'Crypto Trading',
  ENTERTAINMENT: 'Entertainment',
  FOOD_AND_DRINK: 'Food and Drink',
  FUEL: 'Fuel',
  GAMBLING: 'Gambling',
  GROCERIES: 'Groceries',
  HOTELS: 'Hotels',
  INCOME: 'Income',
  INFRASTRUCTURE: 'Infrastructure',
  INSURANCE: 'Insurance',
  INTEREST_PAID: 'Interest Paid',
  INTEREST_RECEIVED: 'Interest Received',
  INVESTMENTS: 'Investments',
  LOAN: 'Loan',
  MCA: 'MCA',
  MORTGAGE: 'Mortgage',
  NSF: 'NSF',
  OTHER: 'Other',
  OVERDRAFT: 'Overdraft',
  PAYDAY: 'Payday',
  PEER_TO_PEER_TRANSFER: 'Peer to Peer Transfer',
  PERSONNEL: 'Personnel',
  REFUND: 'Refund',
  RENT: 'Rent',
  RETAIL: 'Retail',
  RETIREMENT: 'Retirement',
  REVENUE: 'Revenue',
  SALARY: 'Salary',
  SAVINGS: 'Savings',
  SELF_TRANSFER: 'Self Transfer',
  SOCIAL_SECURITY: 'Social Security',
  SWEEP_TRANSFER: 'Sweep Transfer',
  TAX: 'Tax',
  TRADING: 'Trading',
  TRANSPORT: 'Transport',
  UTILITIES: 'Utilities',
  WITHDRAWAL: 'Withdrawal',
};

/**
 * Display text mappings for transaction methods
 * Maps API enum values to user-friendly display text
 */
export const TRANSACTION_METHOD_LABELS: Record<string, string> = {
  BANK_TRANSFER: 'Bank Transfer',
  CARD_PAYMENT: 'Card Payment',
  CASH: 'Cash',
  CHECK: 'Check',
  SELF_TRANSFER: 'Self Transfer',
};

/**
 * Transaction Categories derived from label mappings — single source of truth
 */
export const TRANSACTION_CATEGORIES: FilterOption[] = Object.entries(
  TRANSACTION_CATEGORY_LABELS,
).map(([value, text]) => ({ value, text }));

/**
 * Transaction Methods derived from label mappings — single source of truth
 */
export const TRANSACTION_METHODS: FilterOption[] = Object.entries(
  TRANSACTION_METHOD_LABELS,
).map(([value, text]) => ({ value, text }));

// ============================================================================
// Insight Type Options
// ============================================================================

/**
 * Insight type options for the selector component
 * Each option maps to a ConfigurableInsightLayoutType
 */
export const INSIGHT_TYPE_OPTIONS: Array<{
  value: ConfigurableInsightLayoutType;
  title: string;
  subtitle: string;
}> = [
  {
    value: InsightLayoutType.FLAG_TRANSACTION_ONLY,
    title: 'Transaction',
    subtitle: 'Flag patterns in transaction data',
  },
  {
    value: InsightLayoutType.FLAG_BALANCE_ONLY,
    title: 'Balance',
    subtitle: 'Flag account balance patterns',
  },
  {
    value: InsightLayoutType.FLAG_COMBINED,
    title: 'Combined',
    subtitle: 'Multi-condition logic',
  },
];
